import '@b-stock/fe-core/app-nonce'
import { useMemo, useCallback } from 'react'
import type * as React from 'react'

import type { NextComponentType } from 'next'
import type { AppProps } from 'next/app'

import type { BStockAppProps } from '@b-stock/fe-core/bstock-next/components'
import {
  BStockApp,
  DynamicDevTools,
  GoogleTagManager,
  Braze,
} from '@b-stock/fe-core/bstock-next/components'
import type { GSSPMiddlewareProps } from '@b-stock/fe-core/bstock-next/helpers'
import {
  FeatureFlagProvider,
  getPublicEnvVars,
} from '@b-stock/fe-core/bstock-next/helpers'
import { AnalyticsProvider, ErrorBoundary } from '@b-stock/fe-core/bstock-react'

import { PageTitle } from '@/buyer-portal/components/PageTitle'
import Pendo from '@/buyer-portal/components/Pendo'
import { PusherIdleClose } from '@/components/PusherIdleClose'
import InitFontAwesomeIcons from '@/helpers/css/InitFontAwesomeIcons'
import { FeatureFlags } from '@/helpers/featureFlags'
import { BotDetectionProvider } from '@/helpers/telemetry/BotDetectionProvider'
import GTMIdentity from '@/helpers/telemetry/GTMIdentity'
import InitBrowserLogger from '@/helpers/telemetry/InitBrowserLogger'
import { InteractionGateProvider } from '@/helpers/telemetry/InteractionGateProvider'
import { ListingViewTrackingProvider } from '@/helpers/telemetry/ListingViewTrackingProvider'
import { useLogError } from '@/helpers/useLogError'
import '@/locale/setYupLocale'
import '@b-stock/fe-core/styles/global.css'
import 'react-toastify/dist/ReactToastify.min.css'

import './globals.css'
import { localeData } from '@/locale/localeData'
import { GlobalResets } from '@/styles/globalResets'

/**
 * process.env.NODE_ENV is available on the client.
 * It is not a true environment variable, it is set at build time.
 */
const { NODE_ENV } = process.env

const withWrapper = ({
  Component,
  pageProps: { featureFlags, ldContext, uaString },
}: {
  Component: NextComponentType
} & AppProps<GSSPMiddlewareProps>) => {
  return function WrappedComponent(
    pageProps: React.JSX.IntrinsicAttributes &
      AppProps<GSSPMiddlewareProps> & { children?: React.ReactNode }
  ) {
    const logError = useLogError()

    // Memoize logError to avoid unnecessary re-renders of WrappedComponent
    // Note: Memoization is likely unnecessary now that this is inside WrappedComponent,
    // but kept because it should cause no harm (except for a tiny amount of extra
    // cpu/memory overhead)
    // Feel free to remove if you have time to test to confirm removing is OK.
    // NOTE: Intentional empty dependency, if removed breaking the auction category filter
    // oxlint-disable-next-line react/exhaustive-deps
    const memoizedLogError = useCallback(logError, [])

    const {
      PUBLIC_APP_ENV,
      PUBLIC_BUILD_ID = 'unspecified',
      PUBLIC_BRAZE_API_ENDPOINT,
      PUBLIC_BRAZE_API_KEY,
      PUBLIC_BRAZE_ALLOW_USER_SUPPLIED_JAVASCRIPT,
      PUBLIC_GTM_ID,
      PUBLIC_LAUNCHDARKLY_SDK_CLIENT_SIDE_ID = '',
      PUBLIC_REACT_APP_SEGMENT_WRITE_KEY = '',
      PUBLIC_SEGMENT_CONSENT_BYPASS,
      PUBLIC_PENDO_API_KEY,
    } = getPublicEnvVars()

    const bypassBrazeConsent =
      !!PUBLIC_SEGMENT_CONSENT_BYPASS &&
      PUBLIC_SEGMENT_CONSENT_BYPASS !== 'false'

    const enableBraze =
      featureFlags[FeatureFlags.ENABLE_BRAZE_MARKETING_POPUPS.key] === true

    const isLowerEnvironment =
      NODE_ENV === 'development' ||
      PUBLIC_APP_ENV === 'dev' ||
      PUBLIC_APP_ENV === 'qa'

    return (
      <>
        <GlobalResets />
        <FeatureFlagProvider
          featureFlags={featureFlags}
          clientSideId={PUBLIC_LAUNCHDARKLY_SDK_CLIENT_SIDE_ID}
          context={ldContext}
        >
          <PusherIdleClose />
          <AnalyticsProvider
            segmentWriteKey={PUBLIC_REACT_APP_SEGMENT_WRITE_KEY}
            useGTMConsent={!bypassBrazeConsent}
          >
            <InteractionGateProvider>
              <BotDetectionProvider serverUserAgent={uaString}>
                <ListingViewTrackingProvider>
                  <ErrorBoundary onError={memoizedLogError}>
                    <Braze
                      apiKey={PUBLIC_BRAZE_API_KEY}
                      apiEndpoint={PUBLIC_BRAZE_API_ENDPOINT}
                      bypassConsent={bypassBrazeConsent}
                      allowUserSuppliedJavascript={
                        PUBLIC_BRAZE_ALLOW_USER_SUPPLIED_JAVASCRIPT !== 'false'
                      }
                      enabled={enableBraze}
                    >
                      <PageTitle />
                      <Component {...pageProps} />
                    </Braze>
                  </ErrorBoundary>
                </ListingViewTrackingProvider>
              </BotDetectionProvider>
            </InteractionGateProvider>
          </AnalyticsProvider>
          {PUBLIC_PENDO_API_KEY ? (
            <Pendo apiKey={PUBLIC_PENDO_API_KEY} />
          ) : null}
        </FeatureFlagProvider>
        {PUBLIC_GTM_ID && <GoogleTagManager gtmId={PUBLIC_GTM_ID} />}
        {PUBLIC_GTM_ID && <GTMIdentity />}
        <DynamicDevTools
          appEnv={PUBLIC_APP_ENV}
          buildId={PUBLIC_BUILD_ID}
          nodeEnv={NODE_ENV}
          portal="home-portal"
          visibleByDefault={isLowerEnvironment}
        />
      </>
    )
  }
}

const BuyerApp = (
  appProps: Omit<
    BStockAppProps,
    'theme' | 'locale' | 'localeData' | 'showLoadingSpinner'
  >
) => {
  // WARNING: Do NOT use any hooks here that rely on a context setup by BStockAppProviders
  // (e.g. useAccessToken), otherwise you'll run into problems because it will  use the
  // default values instead of whatever would get set up by BStockApp.
  // If you need to use one of those hooks, add the lookup inside the `WrappedComponent`
  // created by `withWrapper` instead.

  // `appProps` is a new object reference on every render, but `withWrapper`
  // only captures `Component`, `featureFlags`, `ldContext`, and `uaString`.
  // Those values are stable per session, so we only need to recreate
  // `WrappedComponent` when the page component itself changes (i.e. on
  // cross-page navigation). Using the full `appProps` object caused a new
  // function reference every filter/sort change → React remounted the entire
  // page tree → dropdowns closed and search state reset.
  const WrappedComponent = useMemo(
    () => withWrapper(appProps),
    // oxlint-disable-next-line react/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appProps.Component]
  )

  return (
    <>
      <InitBrowserLogger />
      <InitFontAwesomeIcons />
      <BStockApp
        {...appProps}
        localeData={localeData}
        showLoadingSpinner
        Component={WrappedComponent}
      />
    </>
  )
}

export default BuyerApp
